import React from 'react';
import { GetServerSideProps } from 'next';
import BlogHeader from '@/components/blog/BlogHeader';
import BlogBreadCrumbs from '@/components/blog/BlogBreadcrumbs';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { BlogEnum } from '@/types/translations/BlogEnum';
import { AuthorServerSideProps } from '@/types/serverSideProps';
import Container from '@/components/Container';
import Image from 'next/image';
import getAuthorServerSideProps from '@/lib/serverSideProps/serverSidePropsPages/getAuthorServerSideProps';
import BlogCardsGrid from '@/components/blog/BlogCardsGrid';
import { PrimaryButtonLink } from '@/components/common/PrimaryButton';
import { FaArrowRight } from 'react-icons/fa';
import AuthorMeta from '@/components/seo/meta/AuthorMeta';
const Authors = ({ blogContributor }: AuthorServerSideProps) => {
  const { translate } = useTypedTranslation<BlogEnum>('blog');

  return (
    <>
      <AuthorMeta
        authorName={blogContributor.name}
        authorSlug={blogContributor.slug}
        authorImage={blogContributor.mainImageUrl}
        authorDescription={blogContributor.description}
      />
      <BlogHeader />
      <BlogBreadCrumbs
        shareTitle={translate(BlogEnum.authors_share_title)}
        authorName={blogContributor.name}
      />
      <Container>
        <div className="tw-pt-12">
          <div className="tw-flex tw-items-start">
            <div className="max-md:tw-hidden">
              <Image
                src={blogContributor.mainImageUrl}
                width={260}
                height={251}
                alt={blogContributor.name}
                className="tw-rounded-full tw-object-cover"
              />
            </div>
            <div className="md:tw-pl-24 tw-w-full">
              <div className="tw-w-fit max-sm:tw-mx-auto md:tw-pb-4 max-md:tw-mx-auto">
                <h1 className="tw-text-text tw-font-prata tw-font-normal tw-text-3xl max-nine:tw-text-2xl">
                  {blogContributor.name}
                </h1>
                <hr
                  className="max-md:tw-mx-auto tw-h-1 tw-w-4/6 tw-bg-custom-gold tw-rounded-full tw-opacity-100 tw-border-t-0 tw-border-none tw-m-0 tw-p-0 tw-my-4 max-sm:tw-mt-2"
                  aria-hidden="true"
                />
              </div>
              <div className="md:tw-hidden tw-w-full tw-flex tw-justify-center tw-items-center max-md:tw-pb-12">
                <Image
                  src={blogContributor.mainImageUrl}
                  width={260}
                  height={251}
                  alt={blogContributor.name}
                  className="tw-rounded-full tw-object-cover"
                />
              </div>
              <div className="tw-w-full">
                <div className="tw-flex tw-items-stretch tw-justify-between child:tw-basis-[calc(50%-0.5rem)] max-md:child:tw-basis-full tw-flex-wrap tw-gap-2">
                  <div className="tw-bg-customGray tw-border-solid tw-border-[#EAEBF0] tw-border-0.75 tw-p-4 tw-rounded-xl tw-flex tw-flex-col tw-justify-center tw-items-start">
                    <h2 className="tw-text-text tw-font-plusJakartaSans tw-font-semibold tw-text-lg max-nine:!tw-text-base">
                      {translate(BlogEnum.born)}:
                    </h2>
                    <p className="tw-text-slate-gray tw-font-plusJakartaSans tw-text-base max-nine:tw-text-sm">
                      {blogContributor.born}
                    </p>
                  </div>
                  <div className="tw-bg-customGray tw-border-solid tw-border-[#EAEBF0] tw-border-0.75 tw-p-4 tw-rounded-xl tw-flex tw-flex-col tw-justify-center tw-items-start">
                    <h2 className="tw-text-text tw-font-plusJakartaSans tw-font-semibold tw-text-lg max-nine:!tw-text-base">
                      {translate(BlogEnum.favorite_whisky)}:
                    </h2>
                    <p className="tw-text-slate-gray tw-font-plusJakartaSans tw-text-base max-nine:tw-text-sm">
                      {blogContributor.favoriteWhisky}
                    </p>
                  </div>
                  <div className="tw-bg-customGray tw-border-solid tw-border-[#EAEBF0] tw-border-0.75 tw-p-4 tw-rounded-xl tw-flex tw-flex-col tw-justify-center tw-items-start">
                    <h2 className="tw-text-text tw-font-plusJakartaSans tw-font-semibold tw-text-lg max-nine:!tw-text-base">
                      {translate(BlogEnum.nationality)}:
                    </h2>
                    <p className="tw-text-slate-gray tw-font-plusJakartaSans tw-text-base max-nine:tw-text-sm">
                      {blogContributor.nationality}
                    </p>
                  </div>
                  <div className="tw-bg-customGray tw-border-solid tw-border-[#EAEBF0] tw-border-0.75 tw-p-4 tw-rounded-xl tw-flex tw-flex-col tw-justify-center tw-items-start">
                    <h2 className="tw-text-text tw-font-plusJakartaSans tw-font-semibold tw-text-lg max-nine:!tw-text-base">
                      {translate(BlogEnum.whiskyEnthusiasticSince)}:
                    </h2>
                    <p className="tw-text-slate-gray tw-font-plusJakartaSans tw-text-base max-nine:tw-text-sm">
                      {blogContributor.whisky_enthusiastic_since}
                    </p>
                  </div>
                </div>
                <p
                  className="tw-pt-8 tw-text-slate-gray tw-font-medium tw-leading-[26.24px]"
                  dangerouslySetInnerHTML={{
                    __html: blogContributor.description,
                  }}
                ></p>
              </div>
            </div>
          </div>
        </div>
      </Container>
      <Container>
        <div className="tw-pt-12">
          <div className="tw-w-fit tw-pb-12">
            <h2 className="tw-text-text tw-font-plusJakartaSans tw-font-bold tw-text-2xl max-nine:tw-text-sm">
              {translate(BlogEnum.articles_by_this_author)} (
              {blogContributor.blogs.length})
            </h2>
            <hr
              className="tw-h-1 tw-w-4/6 tw-bg-custom-gold tw-rounded-full tw-opacity-100 tw-border-t-0 tw-border-none tw-m-0 tw-p-0 tw-mt-2"
              aria-hidden="true"
            />{' '}
          </div>

          <BlogCardsGrid
            blogs={blogContributor.blogs}
            blogsLoading={false}
            showAll={true}
            showAllButton={false}
          />
        </div>

        <div className="tw-pt-12 tw-flex tw-justify-center tw-items-center">
          <PrimaryButtonLink
            outlined={true}
            href={'/blog/authors'}
            className="tw-flex tw-items-center tw-justify-center"
          >
            {translate(BlogEnum.know_all_authors)}{' '}
            <FaArrowRight className="tw-ml-2" />
          </PrimaryButtonLink>
        </div>
      </Container>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = getAuthorServerSideProps;

export default Authors;
